'use client'
import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Team, teamSchema } from '@basehub/data/replicache/index'
import { useStorage } from '~/hooks/use-storage'
import { useUserStore } from '~/app/_store/user-store'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import {
  PublicTeam,
  publicTeamSchema,
} from '@basehub/data/replicache/mutators/_public-types'
import { getUserTeams } from '~/app/api/ad-hoc/get-user-teams/actions'

type UserTeamsListContext = {
  data: {
    teams: Team[]
    guestTeams: PublicTeam[]
    activeTeam: Team | null
  }
  isLoading: boolean
  isSyncing: boolean
}

const UserTeamsListContext = React.createContext<
  UserTeamsListContext | undefined
>(undefined)

export const UserTeamsListProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const teamSlug = useDashboardSlugs((s) => s.teamSlug)

  // optimistic on localStorage
  const [localTeamsList, setLocalTeamsList] = useStorage(
    'localStorage',
    (z) =>
      z.object({
        teams: teamSchema.array(),
        guestTeams: publicTeamSchema.array(),
      }),
    `user-teams-list-v2-${optimisticUser?.id}`,
    {
      enabled: Boolean(optimisticUser?.id),
      initialValue: null,
    }
  )

  // keeps in sync with real source of truth
  const { isLoading } = useQuery(
    ['user-teams-list', optimisticUser?.id],
    async () => {
      if (optimisticUser?.id.startsWith('playground_')) {
        return {
          teams: [],
          guestTeams: [],
        }
      }
      const response = (await fetch('/api/ad-hoc/get-user-teams').then((res) =>
        res.json()
      )) as ReturnType<typeof getUserTeams> | { error: string }
      if ('error' in response) {
        throw new Error(response.error)
      }
      return response
    },
    {
      enabled: Boolean(optimisticUser?.id),
      onSuccess: (data) => {
        setLocalTeamsList(data)
      },
      refetchInterval: 5000,
      refetchOnMount: false,
    }
  )

  const activeTeam: Team | null = React.useMemo(() => {
    return localTeamsList?.teams?.find((team) => team.slug === teamSlug) ?? null
  }, [localTeamsList, teamSlug])

  return (
    <UserTeamsListContext.Provider
      value={React.useMemo(
        () => ({
          data: {
            teams: localTeamsList?.teams ?? [],
            guestTeams: localTeamsList?.guestTeams ?? [],
            activeTeam,
          },
          isLoading: localTeamsList === null,
          isSyncing: isLoading,
        }),
        [activeTeam, isLoading, localTeamsList]
      )}
    >
      {children}
    </UserTeamsListContext.Provider>
  )
}

export const useUserTeamsList = () => {
  const context = React.useContext(UserTeamsListContext)
  if (!context) {
    throw new Error(
      'useUserTeamsList must be used within a TeamReplicacheProvider'
    )
  }
  return context
}
